import React, { useState, useEffect } from 'react';
import { Bell, X } from 'lucide-react';
import api from '../../services/api';
import { useAuthContext } from '../../contexts/AuthContext';

interface Notification {
  id: number;
  type: string;
  title: string;
  message: string;
  enquiry_id?: number;
  created_at: string;
  is_read: boolean;
}

const NotificationBell: React.FC = () => {
  const { token, loading: authLoading, user } = useAuthContext();
  const [notifications, setNotifications] = useState<Notification[]>([]);
  const [showDropdown, setShowDropdown] = useState(false);
  const [unreadCount, setUnreadCount] = useState(0);
  const [lastNotificationCount, setLastNotificationCount] = useState(0);
  const [hasNotificationAccess, setHasNotificationAccess] = useState(false);

  useEffect(() => {
    // Check notification access when user is available
    if (user && !authLoading) {
      checkNotificationAccess();
    }
  }, [user, authLoading]);

  useEffect(() => {
    // Only start fetching when we have a token, auth is not loading, and user has access
    if (token && !authLoading && hasNotificationAccess) {
      fetchNotifications();
      const interval = setInterval(fetchNotifications, 60000); // Check every 60 seconds
      return () => clearInterval(interval);
    }
  }, [token, authLoading, hasNotificationAccess]);

  const checkNotificationAccess = async () => {
    try {
      const response = await api.get('/role-pages/access/notifications');
      setHasNotificationAccess(response.data.success && response.data.data.has_access);
    } catch (error) {
      setHasNotificationAccess(false);
    }
  };

  const fetchNotifications = async () => {
    // Don't fetch if no token available, auth is still loading, or user doesn't have access
    if (!token || authLoading || !hasNotificationAccess) {
      return;
    }
    
    try {
      // Only fetch unread notifications
      const response = await api.get('/notifications?unread_only=true');
      if (response.data.success) {
        const unreadNotifications = response.data.data;
        const newUnreadCount = unreadNotifications.length;
        
        // Play sound if new notifications arrived
        if (newUnreadCount > lastNotificationCount && lastNotificationCount > 0) {
          try {
            // Try multiple sound approaches
            const audioContext = new (window.AudioContext || (window as any).webkitAudioContext)();
            const oscillator = audioContext.createOscillator();
            const gainNode = audioContext.createGain();
            
            oscillator.connect(gainNode);
            gainNode.connect(audioContext.destination);
            
            oscillator.frequency.setValueAtTime(800, audioContext.currentTime);
            oscillator.frequency.setValueAtTime(600, audioContext.currentTime + 0.1);
            gainNode.gain.setValueAtTime(0.1, audioContext.currentTime);
            gainNode.gain.exponentialRampToValueAtTime(0.01, audioContext.currentTime + 0.3);
            
            oscillator.start(audioContext.currentTime);
            oscillator.stop(audioContext.currentTime + 0.3);
          } catch (error) {
            // Fallback notification (silent)
          }
        }
        
        setNotifications(unreadNotifications);
        setUnreadCount(newUnreadCount);
        setLastNotificationCount(newUnreadCount);
      }
    } catch (error: any) {
      // Only log if it's not a 401 (auth issue during normal flow)
      if (error.response?.status !== 401) {
        console.error('Error fetching notifications:', error);
      }
    }
  };

  const markAsRead = async (notificationId: number) => {
    // Remove the notification from the list immediately for better UX
    setNotifications(prev => prev.filter(n => n.id !== notificationId));
    setUnreadCount(prev => Math.max(0, prev - 1));
    
    try {
      await api.put(`/notifications/${notificationId}`);
    } catch (error) {
      console.error('Error marking notification as read:', error);
      // On error, refetch notifications to restore correct state
      fetchNotifications();
    }
  };

  const getNotificationIcon = (type: string) => {
    switch (type) {
      case 'new_enquiry': return '📝';
      case 'assignment_completed': return '✅';
      default: return '📢';
    }
  };

  // Don't render notification bell - notifications disabled
  return null;
};

export default NotificationBell;